import { useCurrentUser } from '@/hooks/use-auth'
import { apiClient } from '@/lib/api-client'
import { invalidatePaymentQueries } from '@/lib/invalidate-payment-queries'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'

/** 一条审批待办（后端 /v1/approvals/tasks 的 data 元素） */
export interface ApprovalTask {
  orders?: import('@/components/collection-overview').CollectionOrder[]
  orderCount?: number
  taskId: string
  instanceId: string
  flowCode: string
  flowLabel: string
  bizType: string
  bizId: string
  orderId: string | null
  orderNo: string | null
  invoiceNo?: string | null
  orderCreatedAt?: string | null
  centerName?: string | null
  customerName?: string | null
  companyName?: string | null
  amount?: string | null
  action: string
  assigneeId?: string | null
  assigneeName?: string | null
  assigneeRole: string
  createdAt: string
}

function authHeaders() {
  return {
    Authorization: `Bearer ${localStorage.getItem('access_token')}`,
    'Content-Type': 'application/json',
  }
}

/** 当前用户「角色并集」的待处理审批待办（市场核价 / 退款审批）。 */
export function useApprovalTasks(bizId?: string) {
  const { data: user } = useCurrentUser()
  return useQuery<ApprovalTask[]>({
    queryKey: ['approval-tasks', bizId],
    enabled: user?.scope === 'STAFF',
    queryFn: async () => {
      const res = await fetch(`/api/v1/approvals/tasks${bizId ? `?biz_id=${encodeURIComponent(bizId)}` : ''}`, {
        headers: authHeaders(),
      })
      if (!res.ok) throw new Error('审批任务加载失败，请刷新重试')
      const json = await res.json()
      return (json.result?.data as ApprovalTask[]) || []
    },
    staleTime: 15_000,
    refetchInterval: 60_000,
    refetchOnWindowFocus: true,
  })
}

export interface ApprovalTaskDetail extends ApprovalTask {
  canAct?: boolean
  stateLabel?: string
  comment?: string | null
  state: string
  /** 业务对象明细（发票或退款详情；查不到时为 null） */
  bizDetail: Record<string, unknown> | null
}

/** 一条审批待办的审查依据（发票/退款明细），供审查抽屉渲染。 */
export function useApprovalTaskDetail(taskId: string | null) {
  return useQuery<ApprovalTaskDetail | null>({
    queryKey: ['approval-task-detail', taskId],
    enabled: !!taskId,
    queryFn: async () => {
      const res = await fetch(`/api/v1/approvals/tasks/${taskId}`, {
        headers: authHeaders(),
      })
      if (!res.ok) throw new Error('审批详情加载失败，请重试')
      const json = await res.json()
      return (json.result as ApprovalTaskDetail) ?? null
    },
  })
}

/** 通过 / 驳回一条审批待办；成功后刷新待办列表与菜单角标。 */
export function useApprovalAction() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (vars: {
      taskId: string
      decision: 'approve' | 'reject'
      comment?: string
      issuerEntityId?: string
    }) => {
      const res = await fetch(
        `/api/v1/approvals/tasks/${vars.taskId}/${vars.decision}`,
        {
          method: 'POST',
          headers: authHeaders(),
          body: JSON.stringify({
            comment: vars.comment ?? null,
            issuerEntityId: vars.issuerEntityId ?? null,
          }),
        },
      )
      if (!res.ok) {
        const j = await res.json().catch(() => ({}) as Record<string, unknown>)
        const msg =
          (j as { error?: { message?: string }; message?: string })?.error
            ?.message ||
          (j as { message?: string })?.message ||
          '操作失败'
        throw new Error(msg)
      }
      return res.json()
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['approval-tasks'] })
      qc.invalidateQueries({ queryKey: ['task-badges'] })
      void invalidatePaymentQueries(qc)
    },
  })
}

/** Uses the same personal/shared-pool scope, filtering before server pagination. */
export function useApprovalTaskPage(filters: { biz_id?: string; bizType?: string; flow?: string; orderId?: string; keyword?: string; centerId?: string; createdFrom?: string; createdTo?: string; page?: number; limit?: number }) {
  return useQuery<{ data: ApprovalTask[]; total: number }>({
    queryKey: ['approval-tasks', 'page', filters],
    queryFn: () => {
      const params = new URLSearchParams()
      for (const [key, value] of Object.entries(filters)) if (value !== undefined && value !== '') params.set(key, String(value))
      return apiClient.get(`/approvals/tasks?${params}`)
    },
    staleTime: 15_000, refetchInterval: 60_000, refetchOnWindowFocus: true,
  })
}
